import React, {
    FunctionComponent,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { Box, Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { commonStyles } from 'bluesquare-components';
import classnames from 'classnames';
import { isEqual } from 'lodash';

import { getValues, useFormState } from '../../../hooks/form';
import {
    GroupDropdownOption,
    OrgUnitTypeDropdownOption,
} from '../configuration/types';
import { OrgUnit, OrgunitInititialState } from '../types/orgUnit';
import { OrgUnitInfos } from './OrgUnitInfos';

const initialFormState = (
    orgUnit: Partial<OrgUnit> | null,
): OrgunitInititialState => ({
    id: orgUnit?.id ?? 0,
    name: orgUnit?.name ?? '',
    org_unit_type_id: orgUnit?.org_unit_type_id
        ? `${orgUnit.org_unit_type_id}`
        : undefined,
    groups: orgUnit?.groups?.map(g => g.id) ?? [],
    sub_source: orgUnit?.sub_source,
    validation_status: orgUnit?.id ? orgUnit?.validation_status : 'VALID',
    aliases: orgUnit?.aliases ?? ([] as string[]),
    source_id: orgUnit?.source_id,
    parent: orgUnit?.parent,
    source_ref: orgUnit?.source_ref,
    code: orgUnit?.code,
    reference_instance_id: orgUnit?.reference_instance_id,
    opening_date: orgUnit?.opening_date ? orgUnit.opening_date : undefined,
    closed_date: orgUnit?.closed_date ? orgUnit.closed_date : undefined,
});

const useStyles = makeStyles(theme => ({
    ...commonStyles(theme),
}));

type Props = {
    orgUnit: Partial<OrgUnit> | null;
    orgUnitTypes: OrgUnitTypeDropdownOption[];
    groups: GroupDropdownOption[];
    saveOrgUnit: (
        newOu: OrgUnit,
        onSuccess: (unit: OrgUnit) => void,
        onError: (error: any) => void,
    ) => void;
    params: Record<string, string>;
    onResetOrgUnit: () => void;
    isFetchingOrgUnitTypes: boolean;
    isFetchingGroups: boolean;
};

export const OrgUnitForm: FunctionComponent<Props> = ({
    orgUnit,
    orgUnitTypes,
    groups,
    saveOrgUnit,
    params,
    onResetOrgUnit,
    isFetchingOrgUnitTypes,
    isFetchingGroups,
}) => {
    const classes: Record<string, string> = useStyles();
    // Making initialState accessible so we can check if the form values have changed and disable save button accordingly
    const initialState = useMemo(() => initialFormState(orgUnit), [orgUnit]);

    const [formState, setFieldValue, setFieldErrors, setFormState] =
        useFormState(initialState);

    // FormState values as dict. Used to enable comparison with initialState
    const currentStateValues: OrgunitInititialState = useMemo(
        () => getValues(formState) as OrgunitInititialState,
        [formState],
    );
    const [orgUnitModified, setOrgUnitModified] = useState(false);
    const handleSave = () => {
        const newOrgUnit: Record<string, any> = Object.fromEntries(
            Object.entries(formState).map(([key, v]: [string, any]) => {
                const value = 'value' in v ? v.value : v;
                // For opening_date and closed_date, send null if value is undefined/null/empty
                if (key === 'opening_date' || key === 'closed_date') {
                    return [key, value ?? null];
                }
                return [key, value];
            }),
        );
        newOrgUnit.parent_id =
            isNewOrgunit && params.parentOrgUnitId && !newOrgUnit.parent?.id
                ? params.parentOrgUnitId
                : newOrgUnit.parent?.id;
        saveOrgUnit(
            newOrgUnit as OrgUnit,
            savedOrgUnit => {
                setOrgUnitModified(false);
                setFormState(initialFormState(savedOrgUnit));
            },
            error => {
                if (error.status === 400) {
                    error.details.forEach(entry => {
                        setFieldErrors(entry.errorKey, [entry.errorMessage]);
                    });
                }
            },
        );
    };

    const handleChangeField = useCallback(
        (key, value) => {
            const modifiedState: OrgunitInititialState = {
                ...currentStateValues,
                [key]: value,
            };
            const invalidAliases = modifiedState.aliases.includes('');
            if (isEqual(modifiedState, initialState) || invalidAliases) {
                setOrgUnitModified(false);
            } else {
                setOrgUnitModified(true);
            }
            setFieldValue(key, value);
        },
        [setFieldValue, initialState, currentStateValues],
    );

    // TODO change component in blsq-comp library to avoid separate handler
    // This fix assumes we can only add one alias at a time
    const handleChangeAlias = useCallback(
        (key, value) => {
            const orgUnitAliases = orgUnit?.aliases ?? [];
            const newAlias = value[value.length - 1];
            const actualAliases = value.filter(alias => alias !== '');
            const modifiedState = { ...currentStateValues, [key]: value };
            if (newAlias !== '' && !isEqual(actualAliases, orgUnitAliases)) {
                setOrgUnitModified(true);
            } else if (
                isEqual(modifiedState, initialState) ||
                newAlias === ''
            ) {
                setOrgUnitModified(false);
            } else if (
                value.length === 0 &&
                !isEqual(modifiedState, initialState)
            ) {
                setOrgUnitModified(true);
            }
            setFieldValue(key, value);
        },
        [orgUnit?.aliases, setFieldValue, initialState, currentStateValues],
    );

    const handleChangeInfo = useCallback(
        (key, value) => {
            if (key === 'aliases') {
                handleChangeAlias(key, value);
            } else {
                handleChangeField(key, value);
            }
        },
        [handleChangeAlias, handleChangeField],
    );

    const handleReset = () => {
        setOrgUnitModified(false);
        setFormState(initialFormState(orgUnit));
        onResetOrgUnit();
    };

    const isNewOrgunit = params.orgUnitId === '0';

    useEffect(() => {
        if (orgUnit?.id !== formState.id.value) {
            setFormState(initialFormState(orgUnit));
        }
    }, [formState.id.value, orgUnit, orgUnit?.id, setFormState]);
    return (
        <Box pt={isNewOrgunit ? 2 : 0}>
            <Grid
                container
                spacing={0}
                alignItems="center"
                className={classnames(!isNewOrgunit && classes.marginTopBig)}
            >
                {orgUnit && (
                    <OrgUnitInfos
                        params={params}
                        orgUnitState={formState}
                        orgUnit={orgUnit}
                        orgUnitTypes={orgUnitTypes}
                        groups={groups}
                        onChangeInfo={handleChangeInfo}
                        resetTrigger={!orgUnitModified}
                        handleSave={handleSave}
                        handleReset={handleReset}
                        orgUnitModified={orgUnitModified}
                        isFetchingOrgUnitTypes={isFetchingOrgUnitTypes}
                        isFetchingGroups={isFetchingGroups}
                        referenceInstances={orgUnit?.reference_instances ?? []}
                    />
                )}
            </Grid>
        </Box>
    );
};
